/**
 * กระเด็น / ล้ม / ลุก (the bout's knock layer, lane DUEL-ARMOR; data/duelKnock.ts; design
 * docs/design/combat/2026-10-11-duel-armor.md §2). In a bout's fight (`DUEL_FLAGS.v2`) only.
 *
 * - A move with a knock tag that lands unguarded (not in a lock form) throws the one struck: `fling` = an arc back along the
 *   blow (`KNOCK.air`), `down` = a trip (`KNOCK.trip`); then `KNOCK.down` on the ground; then the get-up they chose. Red armour
 *   takes nothing; yellow takes a short stun in place instead (`DUEL_ARMOR.yellowStun`); a fighter `steady` after getting up,
 *   or thrown inside `repeatGap`, takes a short stun and the plain shove instead.
 * - In the air: ONE more blow lands (x`juggleMult`, no new reaction); the rest pass. On the ground: ONE low blow lands
 *   (sweeps, a circle round the striker's feet, a ground-aimed move, a dive strike; x`otgMult`, no new reaction); the rest
 *   pass over the body. Rising, the fighter is hit as anyone is (the blow ends the get-up).
 * - The get-up (read when the down time ends): a dodge pressed while down = roll (i-frames, then a stop, พลังกาย); the basic
 *   cut or a move pressed = the rising attack (untouchable at its start; a whiff or a guard = a recovery); the guard held =
 *   rise guarding; the walk held away from the foe = stay down (once); else the plain rise.
 * - The thrown / downed fighter is held (`stunT` kept up, commands that act are dropped here, the bout's shove is held).
 * Deterministic: no draws; time from the hooks' dt.
 */
import { DUEL_ARMOR } from '../data/duelArmor';
import { DUEL_FLAGS } from '../data/duelMatch';
import { KNOCK, OTG_MOVES, knockTagOf, type KnockKind } from '../data/duelKnock';
import { SKILLS } from '../data/skills';
import type { Entity, PlayerCommand } from '../world';
import { duelAirOf } from './duelAir';
import { duelArmorOf } from './duelArmor';
import { addShoveHold } from './duelHolds';
import { duelLockOf } from './duelLock';
import { duelRecoveryOf } from './duelRecovery';
import type { SystemHost, WorldSystem } from './types';

const SYSTEMS = new WeakMap<object, DuelKnockSystem>();
export const duelKnockOf = (w: object): DuelKnockSystem | null => SYSTEMS.get(w) ?? null;

export type KnockPhase = 'air' | 'trip' | 'down' | 'rise' | 'roll' | 'riseAttack';
export type GetUp = 'rise' | 'guard' | 'roll' | 'attack' | 'stay';

export interface KnockView {
  readonly phase: KnockPhase;
  /** Seconds into the phase, and its length. */
  readonly t: number;
  readonly len: number;
  /** How the fighter went down (the picture: thrown through the air, or tripped). */
  readonly how: KnockKind;
  /** The get-up under way (rise / guard / roll / attack), once chosen. */
  readonly getUp: GetUp | null;
}

interface Knock {
  phase: KnockPhase;
  t: number;
  len: number;
  how: KnockKind;
  /** Unit direction of the throw / the roll, and metres to cover in this phase. */
  ux: number;
  uy: number;
  dist: number;
  /** Covered share of `dist` so far (0-1). */
  done: number;
  juggled: boolean;
  otg: boolean;
  stayed: boolean;
  /** Stay down asked (by the bot or a test) before the down time ends. */
  stayWanted: boolean;
  /** A get-up pressed while down: roll (with its way) or the rising attack. */
  pressed: { kind: 'roll'; dx: number; dy: number } | { kind: 'attack' } | null;
  getUp: GetUp | null;
  foe: number;
}

/** Commands an acting fighter would send; held while thrown or down (the walk and the guard key are read, not dropped). */
const HELD = new Set<PlayerCommand['t']>(['skill', 'attack', 'dash', 'jump', 'engage', 'moveTo']);

export class DuelKnockSystem implements WorldSystem {
  readonly name = 'duelKnock';
  private readonly knocks = new Map<number, Knock>();
  /** Fighter id -> world time until which a new throw becomes a short stun (after getting up). */
  private readonly steadyUntil = new Map<number, number>();
  /** Fighter id -> world time of the latest throw. */
  private readonly lastAt = new Map<number, number>();
  /** Fighters diving this tick (the dive strike reaches a downed body). */
  private diving = new Set<number>();
  /** The blow under way (set before the bout's rules, read after). */
  private pending: { tgt: number; mult: number } | { tgt: number; throw: true } | null = null;
  /** Counts, for the tests and the sims. */
  readonly tally = {
    fling: 0,
    down: 0,
    juggle: 0,
    otg: 0,
    passed: 0,
    steady: 0,
    yellow: 0,
    roll: 0,
    riseAttack: 0,
    stay: 0,
    guard: 0,
    rise: 0,
  };

  constructor(private readonly h: SystemHost) {
    SYSTEMS.set(h.world, this);
  }

  setup(): void {
    const w = this.h.world;
    addShoveHold(
      w,
      (id) => this.knocks.has(id) || (!!this.pending && 'throw' in this.pending && this.pending.tgt === id),
    );
    const prev = w.duelMod;
    w.duelMod = (src, tgt, res, kind, skillId) => {
      this.before(src, tgt, res, skillId);
      prev?.(src, tgt, res, kind, skillId);
      this.after(src, tgt, res, skillId);
      this.pending = null;
    };
  }

  private inBout(id: number): boolean {
    const b = this.h.world.pvpBout;
    return DUEL_FLAGS.v2 && !!b && b.fight && (b.a === id || b.b === id);
  }

  view(id: number): KnockView | null {
    const k = this.knocks.get(id);
    return k ? { phase: k.phase, t: k.t, len: k.len, how: k.how, getUp: k.getUp } : null;
  }

  /** Thrown or down (not yet rising): a lock form or a meaty has nothing to take hold of but the rules above. */
  isDown(id: number): boolean {
    const p = this.knocks.get(id)?.phase;
    return p === 'air' || p === 'trip' || p === 'down';
  }

  /** The bot's (or a test's) get-up choice while down; the player's comes from the keys. */
  choose(id: number, g: GetUp): void {
    const k = this.knocks.get(id);
    if (!k || (k.phase !== 'air' && k.phase !== 'trip' && k.phase !== 'down')) return;
    const foe = this.h.world.get(k.foe);
    const e = this.h.world.get(id);
    if (g === 'roll' && e && foe) {
      // Back from the foe by default.
      const dx = e.x - foe.x;
      const dy = e.y - foe.y;
      const l = Math.hypot(dx, dy) || 1;
      k.pressed = { kind: 'roll', dx: dx / l, dy: dy / l };
    } else if (g === 'attack') k.pressed = { kind: 'attack' };
    else if (g === 'stay') k.stayWanted = true;
    else if (g === 'guard') {
      const inp = this.h.world.players.get(id)?.input;
      if (inp) inp.block = true;
    }
  }

  /**
   * A form's end may hand its foe over (P3 hook): down on the ground from here, with the get-up choices. No-op outside a fight.
   */
  downFrom(id: number, foeId: number): void {
    const e = this.h.world.get(id);
    if (!e || !this.inBout(id)) return;
    this.start(e, foeId, 'down', 0, 0, 0, 'down');
  }

  refuse(cmd: PlayerCommand): boolean {
    const p = this.h.world.player;
    const k = this.knocks.get(p.id);
    if (!k) return false;
    if (k.phase === 'air' || k.phase === 'trip' || k.phase === 'down') {
      if (cmd.t === 'dash') {
        const l = Math.hypot(cmd.dx, cmd.dy);
        if (l > 1e-6) k.pressed = { kind: 'roll', dx: cmd.dx / l, dy: cmd.dy / l };
        else this.choose(p.id, 'roll');
      } else if (cmd.t === 'attack' || cmd.t === 'skill') k.pressed = { kind: 'attack' };
    }
    return HELD.has(cmd.t);
  }

  /** Before the bout's rules: a blow on a thrown or downed body passes, or lands reduced; a throw coming is noted. */
  private before(
    src: Entity,
    tgt: Entity,
    res: { outcome: string; damage: number; stun: number; staminaDamage: number; crit: boolean },
    skillId: string,
  ): void {
    this.pending = null;
    if (res.outcome === 'dodge') return;
    const k = this.knocks.get(tgt.id);
    const pass = () => {
      Object.assign(res, { outcome: 'dodge', damage: 0, stun: 0, staminaDamage: 0, crit: false });
      this.tally.passed++;
    };
    if (k) {
      if (k.phase === 'air') {
        if (k.juggled) return pass();
        k.juggled = true;
        k.len += 0.1;
        this.tally.juggle++;
        this.pending = { tgt: tgt.id, mult: KNOCK.juggleMult };
        return;
      }
      if (k.phase === 'trip' || k.phase === 'down') {
        if (k.otg || !this.low(src, skillId)) return pass();
        k.otg = true;
        this.tally.otg++;
        this.pending = { tgt: tgt.id, mult: KNOCK.otgMult };
        return;
      }
      return; // rising, rolling (i-frames), the rising attack: as anyone
    }
    const tag = knockTagOf(skillId);
    if (
      tag &&
      !tgt.blocking &&
      this.inBout(tgt.id) &&
      !this.inForm(src, tgt) &&
      !duelArmorOf(this.h.world)?.tier(tgt.id) &&
      !this.steady(tgt.id)
    )
      this.pending = { tgt: tgt.id, throw: true };
  }

  /** After the bout's rules: the reduced blow, or the throw. */
  private after(
    src: Entity,
    tgt: Entity,
    res: { outcome: string; damage: number; stun: number; spent?: boolean },
    skillId: string,
  ): void {
    const p = this.pending;
    const landed = (res.outcome === 'hit' || res.outcome === 'interrupt') && res.damage > 0 && !res.spent;
    if (p && 'mult' in p) {
      if (landed) res.damage = Math.max(1, Math.round(res.damage * p.mult));
      res.stun = 0;
      return;
    }
    // A blow that lands on a fighter getting up ends the get-up (no armour in it).
    const rising = this.knocks.get(tgt.id);
    if (rising && landed && (rising.phase === 'rise' || rising.phase === 'riseAttack')) {
      this.knocks.delete(tgt.id);
      tgt.stunT = 0;
    }
    const tag = knockTagOf(skillId);
    if (!tag || !landed || this.knocks.has(tgt.id) || !this.inBout(tgt.id) || this.inForm(src, tgt)) return;
    const tier = duelArmorOf(this.h.world)?.tier(tgt.id) ?? null;
    if (tier === 'red') return;
    if (tier === 'yellow') {
      res.stun = Math.max(res.stun, DUEL_ARMOR.yellowStun);
      this.tally.yellow++;
      return;
    }
    if (this.steady(tgt.id)) {
      res.stun = Math.max(res.stun, KNOCK.steadyStun);
      this.tally.steady++;
      return;
    }
    let dx = tgt.x - src.x;
    let dy = tgt.y - src.y;
    let l = Math.hypot(dx, dy);
    if (l < 1e-6) {
      dx = -tgt.dirX;
      dy = -tgt.dirY;
      l = Math.hypot(dx, dy) || 1;
    }
    res.stun = 0;
    if (tag.kind === 'fling') this.start(tgt, src.id, 'air', dx / l, dy / l, tag.dist ?? 3.5, 'fling');
    else this.start(tgt, src.id, 'trip', dx / l, dy / l, 0, 'down');
  }

  private start(
    e: Entity,
    foe: number,
    phase: KnockPhase,
    ux: number,
    uy: number,
    dist: number,
    how: KnockKind,
  ): void {
    const len = phase === 'air' ? KNOCK.air : phase === 'trip' ? KNOCK.trip : KNOCK.down;
    this.knocks.set(e.id, {
      phase,
      t: 0,
      len,
      how,
      ux,
      uy,
      dist,
      done: 0,
      juggled: false,
      otg: false,
      stayed: false,
      stayWanted: false,
      pressed: null,
      getUp: null,
      foe,
    });
    this.lastAt.set(e.id, this.h.world.time);
    if (how === 'fling') this.tally.fling++;
    else this.tally.down++;
    e.cast = null;
    e.blocking = false;
    e.spinT = 0;
    e.dashT = 0;
    e.stunT = Math.max(e.stunT, len + 0.05);
  }

  private steady(id: number): boolean {
    const now = this.h.world.time;
    return now < (this.steadyUntil.get(id) ?? -1) || now - (this.lastAt.get(id) ?? -99) < KNOCK.repeatGap;
  }

  private inForm(a: Entity, b: Entity): boolean {
    const lk = duelLockOf(this.h.world);
    return !!lk && (lk.inForm(a.id) || lk.inForm(b.id));
  }

  /** A blow that reaches a body on the ground: a sweep, a circle round the striker's own feet, a ground-aimed move, a dive. */
  private low(src: Entity, skillId: string): boolean {
    if (OTG_MOVES.includes(skillId) || this.diving.has(src.id)) return true;
    const s = SKILLS[skillId];
    return !!s && (!!s.ground || (!!s.aoe && !s.range));
  }

  step(dt: number): void {
    const w = this.h.world;
    const air = duelAirOf(w);
    this.diving = new Set();
    const b = w.pvpBout;
    if (air && b) for (const id of [b.a, b.b]) if (air.dive(id)) this.diving.add(id);
    for (const [id, k] of [...this.knocks]) {
      const e = w.get(id);
      if (!e || e.dead || !this.inBout(id)) {
        this.knocks.delete(id);
        continue;
      }
      const t0 = k.t;
      k.t = Math.min(k.len, k.t + dt);
      // The throw and the roll cover their ground eased out (fast first).
      if (k.dist > 0) {
        const f = (u: number) => 1 - (1 - u) * (1 - u);
        const d = k.dist * (f(k.t / k.len) - f(t0 / k.len));
        if (d > 0) {
          const fx = e.dirX;
          const fy = e.dirY;
          this.h.move(e, k.ux * d, k.uy * d);
          e.dirX = fx;
          e.dirY = fy;
        }
      }
      e.blocking = false;
      e.stunT = Math.max(e.stunT, k.len - k.t + 0.05);
      if (k.t < k.len) continue;
      this.next(e, k);
    }
  }

  /** A phase ran out: on to the next. */
  private next(e: Entity, k: Knock): void {
    const w = this.h.world;
    if (k.phase === 'air' || k.phase === 'trip') {
      Object.assign(k, { phase: 'down', t: 0, len: KNOCK.down, dist: 0 });
      return;
    }
    if (k.phase === 'down') {
      const inp = w.players.get(e.id)?.input;
      const foe = w.get(k.foe);
      // Held away from the foe: stay down a little longer (once).
      if (
        !k.pressed &&
        !k.stayed &&
        (k.stayWanted ||
          (inp &&
            foe &&
            inp.dx * (e.x - foe.x) + inp.dy * (e.y - foe.y) > 0.3 * Math.hypot(e.x - foe.x, e.y - foe.y)))
      ) {
        k.stayed = true;
        k.len += KNOCK.stayDown;
        this.tally.stay++;
        return;
      }
      if (k.pressed?.kind === 'roll' && e.stamina >= KNOCK.roll.cost) {
        e.stamina -= KNOCK.roll.cost;
        e.invulnT = Math.max(e.invulnT, KNOCK.roll.time);
        // Never through the foe: a roll toward them turns aside.
        let { dx, dy } = k.pressed;
        if (foe) {
          const fx = foe.x - e.x;
          const fy = foe.y - e.y;
          const fl = Math.hypot(fx, fy) || 1;
          if ((dx * fx + dy * fy) / fl > 0.3) [dx, dy] = [-fy / fl, fx / fl];
        }
        Object.assign(k, {
          phase: 'roll',
          t: 0,
          len: KNOCK.roll.time,
          ux: dx,
          uy: dy,
          dist: KNOCK.roll.dist,
          getUp: 'roll',
        });
        this.tally.roll++;
        return;
      }
      if (k.pressed?.kind === 'attack') {
        e.invulnT = Math.max(e.invulnT, KNOCK.riseAttack.invuln);
        Object.assign(k, {
          phase: 'riseAttack',
          t: 0,
          len: KNOCK.riseAttack.startup,
          dist: 0,
          getUp: 'attack',
        });
        this.tally.riseAttack++;
        return;
      }
      const guard = !!inp?.block;
      Object.assign(k, { phase: 'rise', t: 0, len: KNOCK.rise, dist: 0, getUp: guard ? 'guard' : 'rise' });
      if (guard) this.tally.guard++;
      else this.tally.rise++;
      return;
    }
    // The get-up is over: free, and steady for a while.
    this.knocks.delete(e.id);
    this.steadyUntil.set(e.id, w.time + KNOCK.steady);
    e.stunT = 0;
    if (k.phase === 'roll') duelRecoveryOf(w)?.startStop(e.id, KNOCK.roll.stop, 'getup-roll');
    else if (k.phase === 'riseAttack') this.riseStrike(e, k);
    else if (k.getUp === 'guard') {
      const inp = w.players.get(e.id)?.input;
      if (inp?.block) e.blocking = true;
    }
  }

  /** The rising attack's blow: a low cut at the foe if in reach; found nothing or a guard: a recovery. */
  private riseStrike(e: Entity, k: Knock): void {
    const w = this.h.world;
    const foe = w.get(k.foe);
    let out: string | null = null;
    if (foe && !foe.dead && Math.hypot(foe.x - e.x, foe.y - e.y) <= KNOCK.riseAttack.reach) {
      this.h.face(e, foe);
      e.swingT = 0.25;
      out = this.h.hit(e, foe, KNOCK.riseAttack.power, 'attack', 'basic');
    }
    if (out !== 'hit' && out !== 'interrupt')
      duelRecoveryOf(w)?.startStop(e.id, KNOCK.riseAttack.recovery, 'getup-attack');
  }
}
